'use client';

import { useState } from 'react';
import {
  trackEmergencyFundCalculatorCalculate,
  trackEmergencyFundCalculatorReset,
} from 'components/analytics/eventHandlerTracking';
import { CalculatorShell } from 'components/Widget/shared/CalculatorShell';
import { parseCurrency } from 'components/Widget/shared/currency';
import { deferTracking } from 'components/Widget/shared/deferTracking';
import { useCurrencyFields } from 'components/Widget/shared/useCurrencyFields';
import { usePanelSwapFocus } from 'components/Widget/shared/usePanelSwapFocus';
import type { CalculatorFieldErrors, CalculatorFieldValues } from 'components/Widget/shared/types';
import { calculateEmergencyFund } from './emergencyFund';
import { EmergencyFundCalculatorForm } from './EmergencyFundCalculatorForm';
import { EmergencyFundCalculatorResult } from './EmergencyFundCalculatorResult';
import { EmergencyFundCalculatorSummary } from './EmergencyFundCalculatorSummary';
import {
  DEFAULT_GOAL_KEY,
  DISCLAIMER,
  EMERGENCY_FUND_FIELDS,
  INITIAL_VALUES,
  MAX_FIELD_VALUE,
  RANGE_ERROR,
} from './constants';
import type { EmergencyFundState, FundGoalKey } from './types';

const INITIAL_STATE: EmergencyFundState = { errors: {}, goalKey: null };

const validateValues = (values: CalculatorFieldValues): CalculatorFieldErrors => {
  const errors: CalculatorFieldErrors = {};

  // Sanitization strips negative signs, so only the ceiling needs checking. No field is required.
  EMERGENCY_FUND_FIELDS.forEach(({ label }) => {
    if (parseCurrency(values[label]) > MAX_FIELD_VALUE) errors[label] = RANGE_ERROR;
  });

  return errors;
};

export const EmergencyFundCalculator = () => {
  const [state, setState] = useState<EmergencyFundState>(INITIAL_STATE);
  const { values, reset, fieldProps } = useCurrencyFields(INITIAL_VALUES);
  // Derived rather than stored, because the duration picker re-runs it against the same values.
  const panel =
    state.goalKey === null ? null : { goalKey: state.goalKey, result: calculateEmergencyFund(values, state.goalKey) };
  const { formRef, summaryRef, markPanelSwap } = usePanelSwapFocus(panel !== null);

  const handleCalculate = () => {
    const errors = validateValues(values);
    const hasErrors = Object.keys(errors).length > 0;

    setState({ errors, goalKey: hasErrors ? null : DEFAULT_GOAL_KEY });

    deferTracking(() => {
      trackEmergencyFundCalculatorCalculate();
    });

    if (hasErrors) return;

    markPanelSwap();
  };

  const handleReset = () => {
    setState(INITIAL_STATE);
    reset();

    deferTracking(() => {
      trackEmergencyFundCalculatorReset({ label: 'Reset' });
    });
  };

  /** Legacy's Start over returns to the form with the amounts intact; Reset is the clearing action. */
  const handleStartOver = () => {
    markPanelSwap();
    setState(INITIAL_STATE);

    deferTracking(() => {
      trackEmergencyFundCalculatorReset({ label: 'Start Over' });
    });
  };

  const handleGoalKeyChange = (goalKey: FundGoalKey) => setState(previous => ({ ...previous, goalKey }));

  return (
    <CalculatorShell
      title="Emergency Fund Calculator"
      disclaimer={DISCLAIMER}
      summaryRef={summaryRef}
      summary={panel !== null && <EmergencyFundCalculatorSummary result={panel.result} />}>
      {panel === null ? (
        <EmergencyFundCalculatorForm
          ref={formRef}
          errors={state.errors}
          fieldProps={fieldProps}
          onCalculate={handleCalculate}
          onReset={handleReset}
        />
      ) : (
        <EmergencyFundCalculatorResult
          result={panel.result}
          goalKey={panel.goalKey}
          onGoalKeyChange={handleGoalKeyChange}
          onStartOver={handleStartOver}
        />
      )}
    </CalculatorShell>
  );
};
